import React from 'react';
import { ThemeProvider } from '../../context/ThemeContext';
import Header from '../layout/Header';
import Footer from '../layout/Footer';

const PrivacyPolicyContent: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
      <Header />
      <main className="container mx-auto px-4 py-16 max-w-3xl">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Privacy Policy</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-10">Ultimo aggiornamento: agosto 2026</p>

        <div className="prose prose-gray dark:prose-invert max-w-none space-y-8 text-gray-700 dark:text-gray-300">

          <section>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">1. Titolare del trattamento</h2>
            <p>
              Il presente sito è gestito da Walter Rodeghiero, a titolo personale.
              Per qualsiasi richiesta relativa alla privacy puoi scrivere a{' '}
              <a href="mailto:walter@walterdev.eu" className="text-primary-600 dark:text-primary-400 hover:underline">
                walter@walterdev.eu
              </a>.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">2. Dati raccolti e finalità</h2>

            <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200 mt-4 mb-2">Statistiche di visita (server proprio)</h3>
            <p>
              Il sito raccoglie in modo automatico alcune informazioni tecniche per comprendere come viene utilizzato:
              pagina visitata, pagina di provenienza (referrer), lingua del browser, tipo di dispositivo,
              risoluzione dello schermo e durata approssimativa della visita.
              Questi dati vengono memorizzati su un database privato (MySQL) ospitato sul server del titolare
              e non vengono condivisi con terze parti.
              L'identificativo di sessione utilizzato è temporaneo e viene eliminato alla chiusura del browser.
            </p>

            <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200 mt-4 mb-2">Google Analytics 4</h3>
            <p>
              Il sito utilizza Google Analytics 4 (Google LLC), che per impostazione predefinita non memorizza
              l'indirizzo IP completo dei visitatori. Google Analytics raccoglie dati aggregati sull'utilizzo
              del sito tramite cookie di terza parte.
              Per informazioni sul trattamento di Google consulta la{' '}
              <a
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 dark:text-primary-400 hover:underline"
              >
                Privacy Policy di Google
              </a>.
              Puoi disabilitare Google Analytics tramite il{' '}
              <a
                href="https://tools.google.com/dlpage/gaoptout"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 dark:text-primary-400 hover:underline"
              >
                componente aggiuntivo per la disattivazione
              </a>.
            </p>

            <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200 mt-4 mb-2">Preferenze dell'utente</h3>
            <p>
              La preferenza sul tema grafico (chiaro/scuro) viene salvata nel <code>localStorage</code> del browser
              esclusivamente sul dispositivo dell'utente e non viene trasmessa al server.
            </p>

            <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200 mt-4 mb-2">Form di contatto</h3>
            <p>
              Se compili il modulo di contatto, i dati inseriti (nome, email, messaggio) vengono utilizzati
              esclusivamente per rispondere alla tua richiesta e non vengono conservati oltre il tempo
              necessario alla gestione della comunicazione.
            </p>

            <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200 mt-4 mb-2">Assistenti conversazionali basati su intelligenza artificiale</h3>
            <p>
              Alcune applicazioni del sito (chat "Walter AI" del portfolio, Consulente AI di CalCost, assistente
              vocale WBall e assistente di prenotazione CuraPiù) utilizzano un sistema di intelligenza artificiale
              (Google Gemini) per generare risposte testuali o vocali. In conformità al Regolamento (UE) 2024/1689
              (AI Act), sei informato che stai interagendo con un sistema di IA e non con una persona reale.
              I messaggi che invii vengono trasmessi ai server di Google per generare la risposta; per il relativo
              trattamento consulta la{' '}
              <a
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 dark:text-primary-400 hover:underline"
              >
                Privacy Policy di Google
              </a>. Le conversazioni testuali possono essere salvate sul database del titolare per migliorare il
              servizio; l'audio delle sessioni vocali WBall non viene registrato in modo permanente.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">3. Base giuridica</h2>
            <p>
              Il trattamento delle statistiche di visita avviene sulla base del legittimo interesse del titolare
              (art. 6, par. 1, lett. f GDPR) a monitorare il corretto funzionamento del sito e a migliorarne
              i contenuti, senza identificare i singoli visitatori.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">4. Conservazione dei dati</h2>
            <p>
              I dati delle statistiche di visita vengono conservati per un periodo massimo di 12 mesi,
              dopodiché vengono eliminati o aggregati in forma anonima.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">5. Diritti dell'interessato</h2>
            <p>
              In conformità al GDPR hai diritto di accedere ai tuoi dati, richiederne la rettifica o
              la cancellazione, opporti al trattamento e presentare reclamo al Garante per la Protezione
              dei Dati Personali (<a href="https://www.garanteprivacy.it" target="_blank" rel="noopener noreferrer" className="text-primary-600 dark:text-primary-400 hover:underline">www.garanteprivacy.it</a>).
              Per esercitare questi diritti scrivi a{' '}
              <a href="mailto:walter@walterdev.eu" className="text-primary-600 dark:text-primary-400 hover:underline">
                walter@walterdev.eu
              </a>.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">6. Modifiche alla policy</h2>
            <p>
              Questa pagina può essere aggiornata in caso di modifiche al sito o alla normativa applicabile.
              La data di ultimo aggiornamento è indicata in cima alla pagina.
            </p>
          </section>

        </div>
      </main>
      <Footer />
    </div>
  );
};

const PrivacyPolicy: React.FC = () => (
  <ThemeProvider>
    <PrivacyPolicyContent />
  </ThemeProvider>
);

export default PrivacyPolicy;
